import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSettings } from '../hooks/useSettings';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Tag, Type, Hash, Calendar, List, CheckSquare, Eye, EyeOff, Info, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reglages = () => {
  const { settings, updateSettings, getPatientLabelPlural, getNewPatientLabel, getPatientLabel, areStatusesEnabled } = useSettings();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectKey, setSelectKey] = useState(0); // Force re-render key
  
  // Password change states
  const [isPasswordChangeOpen, setIsPasswordChangeOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Team management states
  const [teamData, setTeamData] = useState({ members: [], max_team_size: 1, current_size: 0 });
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [newMember, setNewMember] = useState({ username: '', full_name: '' });
  const [memberLoading, setMemberLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);

  // Custom fields states
  const [customFields, setCustomFields] = useState([]);
  const [isAddFieldOpen, setIsAddFieldOpen] = useState(false);
  const [isEditFieldOpen, setIsEditFieldOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [newField, setNewField] = useState({
    name: '',
    field_key: '',
    field_type: 'text',
    options: [],
    is_required: false
  });
  const [editField, setEditField] = useState({
    name: '',
    field_type: 'text',
    options: [],
    is_required: false
  });
  const [fieldLoading, setFieldLoading] = useState(false);

  // Note statuses states
  const [noteStatuses, setNoteStatuses] = useState([]);
  const [isAddStatusOpen, setIsAddStatusOpen] = useState(false);
  const [isEditStatusOpen, setIsEditStatusOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [newStatus, setNewStatus] = useState({
    name: '',
    color: '#3B82F6',
    order: 0,
    is_default: false
  });
  const [editStatus, setEditStatus] = useState({
    name: '',
    color: '#3B82F6',
    order: 0,
    is_default: false
  });
  const [statusLoading, setStatusLoading] = useState(false);

  // Handle patient type change with debounce to avoid ResizeObserver errors
  const handlePatientTypeChange = (value) => {
    updateSettings({type_patient_label: value});
    // Force complete re-render of Select component
    setTimeout(() => {
      setSelectKey(prev => prev + 1);
    }, 100);
  };

  // Password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    try {
      setPasswordLoading(true);
      const response = await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });

      toast.success(`Mot de passe modifié avec succès ! ${response.data.notes_re_encrypted} notes mises à jour.`);
      
      // Reset form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setIsPasswordChangeOpen(false);

      // Update token if provided
      if (response.data.new_access_token) {
        localStorage.setItem('auth_token', response.data.new_access_token);
      }

    } catch (error) {
      console.error('Password change error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Note status CRUD operations
  const handleCreateStatus = async (e) => {
    e.preventDefault();
    try {
      setStatusLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/note-statuses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newStatus)
      });
      
      if (response.ok) {
        toast.success('Statut créé avec succès');
        fetchNoteStatuses();
        setIsAddStatusOpen(false);
        setNewStatus({ name: '', color: '#3B82F6', order: 0, is_default: false });
      }
    } catch (error) {
      toast.error('Erreur lors de la création du statut');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    try {
      setStatusLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/note-statuses/${editingStatus.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editStatus)
      });
      
      if (response.ok) {
        toast.success('Statut modifié avec succès');
        fetchNoteStatuses();
        setIsEditStatusOpen(false);
        setEditingStatus(null);
      }
    } catch (error) {
      toast.error('Erreur lors de la modification du statut');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleDeleteStatus = async (statusId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce statut ? Il sera retiré de toutes les notes qui l\'utilisent.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/note-statuses/${statusId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        toast.success('Statut supprimé avec succès');
        fetchNoteStatuses();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression du statut');
    }
  };

  const handleEditStatusClick = (status) => {
    setEditingStatus(status);
    setEditStatus({
      name: status.name,
      color: status.color,
      order: status.order,
      is_default: status.is_default
    });
    setIsEditStatusOpen(true);
  };

  const resetPasswordForm = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  // Load team members
  const loadTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team`);
      setTeamData(response.data);
    } catch (error) {
      console.error('Error loading team:', error);
      if (error.response?.status !== 403 && error.response?.status !== 400) {
        toast.error('Erreur lors du chargement de l\'équipe');
      }
    }
  };

  // Add team member
  const handleAddMember = async (e) => {
    e.preventDefault();
    
    if (!newMember.username.includes('@')) {
      toast.error('Veuillez saisir une adresse email valide');
      return;
    }

    try {
      setMemberLoading(true);
      await axios.post(`${API}/team/member`, newMember);
      toast.success('Membre ajouté avec succès ! Un email avec les identifiants a été envoyé.');
      setNewMember({ username: '', full_name: '' });
      setIsAddMemberOpen(false);
      await loadTeamMembers();
    } catch (error) {
      console.error('Error adding member:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout du membre');
    } finally {
      setMemberLoading(false);
    }
  };

  // Delete team member
  const handleDeleteMember = async () => {
    if (!memberToDelete) return;

    try {
      await axios.delete(`${API}/team/member/${memberToDelete.id}`);
      toast.success(`${memberToDelete.username} et toutes ses données ont été supprimés`);
      setDeleteConfirmOpen(false);
      setMemberToDelete(null);
      await loadTeamMembers();
    } catch (error) {
      console.error('Error deleting member:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Load team on mount if user is admin
  useEffect(() => {
    if (user?.role === 'admin') {
      loadTeamMembers();
    }
  }, [user]);

  // Custom fields management


  // Fetch note statuses
  const fetchNoteStatuses = async () => {
    try {
      const token = localStorage.getItem('auth_token'); // Use correct token key
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/note-statuses`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setNoteStatuses(data);
      }
    } catch (error) {
      console.error('Error fetching note statuses:', error);
    }
  };

  const loadCustomFields = async () => {
    try {
      const response = await axios.get(`${API}/custom-fields`);
      setCustomFields(response.data);
    } catch (error) {
      console.error('Error loading custom fields:', error);
      toast.error('Erreur lors du chargement des champs personnalisés');
    }
  };

  const handleAddField = async () => {
    // Validation
    if (!newField.name.trim()) {
      toast.error('Le nom du champ est requis');
      return;
    }

    // Generate field_key from name if not provided
    const fieldKey = newField.field_key || newField.name.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9]/g, '_'); // Replace non-alphanumeric with underscore

    try {
      setFieldLoading(true);
      // Clean up options by filtering empty lines
      const cleanedOptions = newField.options.filter(o => o.trim());
      
      const response = await axios.post(`${API}/custom-fields`, {
        ...newField,
        options: cleanedOptions,
        field_key: fieldKey
      });
      
      setCustomFields([...customFields, response.data]);
      toast.success('Champ ajouté avec succès');
      setIsAddFieldOpen(false);
      setNewField({
        name: '',
        field_key: '',
        field_type: 'text',
        options: [],
        is_required: false
      });
    } catch (error) {
      console.error('Error adding field:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout du champ');
    } finally {
      setFieldLoading(false);
    }
  };

  const handleToggleField = async (field) => {
    try {
      const response = await axios.put(`${API}/custom-fields/${field.id}`, {
        is_active: !field.is_active
      });
      
      setCustomFields(customFields.map(f => 
        f.id === field.id ? response.data : f
      ));
      toast.success(`Champ ${field.is_active ? 'désactivé' : 'activé'}`);
    } catch (error) {
      console.error('Error toggling field:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  const handleOpenEditField = (field) => {
    setEditingField(field);
    setEditField({
      name: field.name,
      field_type: field.field_type,
      options: field.options || [],
      is_required: field.is_required
    });
    setIsEditFieldOpen(true);
  };

  const handleUpdateField = async () => {
    if (!editField.name.trim()) {
      toast.error('Le nom du champ est requis');
      return;
    }

    try {
      setFieldLoading(true);
      // Clean up options by filtering empty lines
      const cleanedOptions = editField.options.filter(o => o.trim());
      
      const response = await axios.put(`${API}/custom-fields/${editingField.id}`, {
        ...editField,
        options: cleanedOptions
      });
      
      setCustomFields(customFields.map(f => 
        f.id === editingField.id ? response.data : f
      ));
      toast.success('Champ modifié avec succès');
      setIsEditFieldOpen(false);
      setEditingField(null);
      setEditField({
        name: '',
        field_type: 'text',
        options: [],
        is_required: false
      });
    } catch (error) {
      console.error('Error updating field:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification du champ');
    } finally {
      setFieldLoading(false);
    }
  };

  const handleDeleteField = async (field) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le champ "${field.name}" ? Les données associées seront perdues.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/custom-fields/${field.id}`);
      setCustomFields(customFields.filter(f => f.id !== field.id));
      toast.success('Champ supprimé avec succès');
    } catch (error) {
      console.error('Error deleting field:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Load custom fields on mount
  useEffect(() => {
    loadCustomFields();
    fetchNoteStatuses();  // Load note statuses
  }, []);

  return (
    <div className="py-6">
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-4">
          <h2 className="text-3xl font-bold text-gray-900">Réglages</h2>
          <p className="text-gray-600 mt-1">Configurez les paramètres de votre application</p>
        </div>

        {/* Settings Card */}
        <Card>
          <CardHeader>
            <CardTitle>Paramètres généraux</CardTitle>
            <CardDescription>
              Personnalisez l'affichage et le comportement de l'application
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Patient Type Setting */}
            <div className="space-y-2">
              <Label htmlFor="patient-type">Type de patient/élève</Label>
              <Select 
                key={`patient-type-${selectKey}`}
                value={settings.type_patient_label} 
                onValueChange={handlePatientTypeChange}
              >
                <SelectTrigger data-testid="patient-type-select">
                  <SelectValue placeholder="Sélectionner le type" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px] overflow-hidden" sideOffset={5}>
                  <SelectItem value="élève">Élève</SelectItem>
                  <SelectItem value="patient">Patient</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="employé">Employé</SelectItem>
                  <SelectItem value="bénéficiaire">Bénéficiaire</SelectItem>
                  <SelectItem value="apprenant">Apprenant</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500">
                Ce terme sera utilisé dans toute l'application pour désigner les personnes que vous suivez.
              </p>
            </div>

            {/* Preview Section */}
            <div className="border-t pt-6">
              <h4 className="font-medium mb-3">Aperçu</h4>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <p className="text-sm">
                  • Mes <span className="font-medium">{getPatientLabelPlural()}</span>
                </p>
                <p className="text-sm">
                  • <span className="font-medium">{getNewPatientLabel()}</span>
                </p>
                <p className="text-sm">
                  • Notes pour ce <span className="font-medium">{getPatientLabel()}</span>
                </p>
              </div>
            </div>

            {/* Auto-save info */}
            <div className="flex justify-end pt-4 border-t">
              <p className="text-sm text-gray-600 italic flex items-center gap-2">
                <Info className="h-4 w-4" />
                Les modifications sont sauvegardées automatiquement
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Security Settings Card */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              🔒 Sécurité
            </CardTitle>
            <CardDescription>
              Gérez vos paramètres de sécurité et de connexion
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h3 className="font-medium">Mot de passe</h3>
                  <p className="text-sm text-gray-600">
                    Modifiez votre mot de passe. Toutes vos notes seront automatiquement re-chiffrées avec le nouveau mot de passe.
                  </p>
                </div>
                <Dialog open={isPasswordChangeOpen} onOpenChange={setIsPasswordChangeOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" onClick={() => setIsPasswordChangeOpen(true)}>
                      Changer le mot de passe
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Changer le mot de passe</DialogTitle>
                      <DialogDescription>
                        ⚠️ Important : Toutes vos notes (élèves et groupes) seront automatiquement re-chiffrées avec le nouveau mot de passe.
                      </DialogDescription>
                    </DialogHeader>
                    
                    <form onSubmit={handlePasswordChange} className="space-y-4">
                      <div>
                        <Label htmlFor="current-password">Mot de passe actuel *</Label>
                        <Input
                          id="current-password"
                          type="password"
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                          placeholder="Saisissez votre mot de passe actuel"
                          required
                          disabled={passwordLoading}
                        />
                      </div>

                      <div>
                        <Label htmlFor="new-password">Nouveau mot de passe *</Label>
                        <Input
                          id="new-password"
                          type="password"
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                          placeholder="Minimum 8 caractères"
                          required
                          disabled={passwordLoading}
                          minLength={8}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Le mot de passe doit contenir au moins 8 caractères
                        </p>
                      </div>

                      <div>
                        <Label htmlFor="confirm-password">Confirmer le nouveau mot de passe *</Label>
                        <Input
                          id="confirm-password"
                          type="password"
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                          placeholder="Confirmez votre nouveau mot de passe"
                          required
                          disabled={passwordLoading}
                        />
                      </div>

                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <div className="text-yellow-600 mt-0.5">⚠️</div>
                          <div className="text-sm text-yellow-800">
                            <p className="font-medium mb-1">Ce qui va se passer :</p>
                            <ul className="text-xs space-y-1">
                              <li>• Votre mot de passe sera mis à jour</li>
                              <li>• Toutes vos notes seront automatiquement re-chiffrées</li>
                              <li>• Vous resterez connecté avec le nouveau mot de passe</li>
                              <li>• Ce processus peut prendre quelques secondes</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button 
                          type="submit" 
                          className="flex-1" 
                          disabled={passwordLoading}
                        >
                          {passwordLoading ? 'Changement en cours...' : 'Changer le mot de passe'}
                        </Button>
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => {
                            resetPasswordForm();
                            setIsPasswordChangeOpen(false);
                          }}
                          disabled={passwordLoading}
                        >
                          Annuler
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Team Management Card - Only for admins with team enabled */}
        {user?.role === 'admin' && teamData.max_team_size > 1 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Gestion d'équipe
              </CardTitle>
              <CardDescription>
                Gérez les membres de votre équipe ({teamData.current_size}/{teamData.max_team_size})
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Add Member Button */}
                <Dialog open={isAddMemberOpen} onOpenChange={setIsAddMemberOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="w-full"
                      disabled={teamData.current_size >= teamData.max_team_size}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Ajouter un membre
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Ajouter un membre à l'équipe</DialogTitle>
                      <DialogDescription>
                        Un email avec les identifiants sera envoyé automatiquement
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleAddMember} className="space-y-4">
                      <div>
                        <Label htmlFor="member-email">Email (utilisé comme identifiant) *</Label>
                        <Input
                          id="member-email"
                          type="email"
                          value={newMember.username}
                          onChange={(e) => setNewMember({...newMember, username: e.target.value})}
                          placeholder="exemple@email.com"
                          required
                          disabled={memberLoading}
                        />
                      </div>
                      <div>
                        <Label htmlFor="member-name">Nom complet *</Label>
                        <Input
                          id="member-name"
                          type="text"
                          value={newMember.full_name}
                          onChange={(e) => setNewMember({...newMember, full_name: e.target.value})}
                          placeholder="Jean Dupont"
                          required
                          disabled={memberLoading}
                        />
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm text-blue-800">
                          ℹ️ Un mot de passe temporaire sera généré et envoyé par email. Le membre devra le changer à sa première connexion.
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" disabled={memberLoading} className="flex-1">
                          {memberLoading ? 'Création...' : 'Créer le compte'}
                        </Button>
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => {
                            setIsAddMemberOpen(false);
                            setNewMember({ username: '', full_name: '' });
                          }}
                          disabled={memberLoading}
                        >
                          Annuler
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>

                {/* Team Members List */}
                {teamData.members.length > 0 && (
                  <div className="border rounded-lg divide-y">
                    {teamData.members.map((member) => (
                      <div key={member.id} className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-400 to-indigo-600 flex items-center justify-center text-white font-semibold">
                            {member.username[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium">{member.username}</p>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Badge variant={member.role === 'admin' ? 'default' : 'secondary'}>
                                {member.role === 'admin' ? 'Administrateur' : 'Membre'}
                              </Badge>
                              {member.last_login && (
                                <span>• Dernière connexion: {new Date(member.last_login).toLocaleDateString()}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        {member.role !== 'admin' && (
                          <Dialog open={deleteConfirmOpen && memberToDelete?.id === member.id} onOpenChange={(open) => {
                            setDeleteConfirmOpen(open);
                            if (!open) setMemberToDelete(null);
                          }}>
                            <DialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setMemberToDelete(member)}
                                className="text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Confirmer la suppression</DialogTitle>
                                <DialogDescription>
                                  Cette action est irréversible
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                  <div className="flex items-start gap-3">
                                    <div className="text-red-600 mt-0.5">⚠️</div>
                                    <div className="text-sm text-red-800 space-y-2">
                                      <p className="font-medium">Attention : Cette action va supprimer définitivement :</p>
                                      <ul className="list-disc list-inside space-y-1">
                                        <li>Le compte de <strong>{member.username}</strong></li>
                                        <li>Tous ses patients/élèves</li>
                                        <li>Tous ses groupes</li>
                                        <li>Toutes ses notes</li>
                                      </ul>
                                      <p className="font-medium mt-2">Cette action ne peut pas être annulée.</p>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    variant="destructive"
                                    onClick={handleDeleteMember}
                                    className="flex-1"
                                  >
                                    Supprimer définitivement
                                  </Button>
                                  <Button
                                    variant="outline"
                                    onClick={() => {
                                      setDeleteConfirmOpen(false);
                                      setMemberToDelete(null);
                                    }}
                                  >
                                    Annuler
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Custom Fields Management */}
        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Champs personnalisés</CardTitle>
                <CardDescription>
                  Gérez les informations que vous souhaitez saisir pour chaque {getPatientLabel()}
                </CardDescription>
              </div>
              <Dialog open={isAddFieldOpen} onOpenChange={setIsAddFieldOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Ajouter un champ
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nouveau champ personnalisé</DialogTitle>
                    <DialogDescription>
                      Créez un nouveau champ pour enrichir vos fiches {getPatientLabelPlural()}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="field-name">Nom du champ</Label>
                      <Input
                        id="field-name"
                        placeholder="Ex: Numéro de téléphone, Adresse..."
                        value={newField.name}
                        onChange={(e) => setNewField({...newField, name: e.target.value})}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="field-type">Type de champ</Label>
                      <Select 
                        value={newField.field_type} 
                        onValueChange={(value) => setNewField({...newField, field_type: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Texte</SelectItem>
                          <SelectItem value="number">Nombre</SelectItem>
                          <SelectItem value="date">Date</SelectItem>
                          <SelectItem value="select">Liste déroulante</SelectItem>
                          <SelectItem value="checkbox">Case à cocher</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {newField.field_type === 'select' && (
                      <div className="space-y-2">
                        <Label>Options (une par ligne)</Label>
                        <textarea
                          className="w-full min-h-[100px] p-2 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder={"Option 1\nOption 2\nOption 3"}
                          value={newField.options.join('\n')}
                          onChange={(e) => setNewField({
                            ...newField, 
                            options: e.target.value.split('\n')
                          })}
                          onKeyDown={(e) => {
                            // Empêcher la soumission du formulaire lors de l'appui sur Entrée
                            if (e.key === 'Enter') {
                              e.stopPropagation();
                            }
                          }}
                          rows={5}
                        />
                        <p className="text-xs text-gray-500">
                          Appuyez sur Entrée pour créer une nouvelle option. {newField.options.length} option(s) ajoutée(s).
                        </p>
                      </div>
                    )}

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="field-required"
                        checked={newField.is_required}
                        onChange={(e) => setNewField({...newField, is_required: e.target.checked})}
                        className="rounded"
                      />
                      <Label htmlFor="field-required" className="cursor-pointer">
                        Champ obligatoire
                      </Label>
                    </div>

                    <div className="flex gap-2 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => setIsAddFieldOpen(false)}
                        className="flex-1"
                      >
                        Annuler
                      </Button>
                      <Button
                        onClick={handleAddField}
                        disabled={fieldLoading}
                        className="flex-1"
                      >
                        {fieldLoading ? 'Ajout...' : 'Ajouter'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Edit Field Dialog */}
              <Dialog open={isEditFieldOpen} onOpenChange={setIsEditFieldOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Modifier le champ personnalisé</DialogTitle>
                    <DialogDescription>
                      Modifiez les propriétés du champ "{editingField?.name}"
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-field-name">Nom du champ</Label>
                      <Input
                        id="edit-field-name"
                        placeholder="Ex: Numéro de téléphone, Adresse..."
                        value={editField.name}
                        onChange={(e) => setEditField({...editField, name: e.target.value})}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="edit-field-type">Type de champ</Label>
                      <Select 
                        value={editField.field_type} 
                        onValueChange={(value) => setEditField({...editField, field_type: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Texte</SelectItem>
                          <SelectItem value="number">Nombre</SelectItem>
                          <SelectItem value="date">Date</SelectItem>
                          <SelectItem value="select">Liste déroulante</SelectItem>
                          <SelectItem value="checkbox">Case à cocher</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {editField.field_type === 'select' && (
                      <div className="space-y-2">
                        <Label>Options (une par ligne)</Label>
                        <textarea
                          className="w-full min-h-[100px] p-2 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder={"Option 1\nOption 2\nOption 3"}
                          value={editField.options.join('\n')}
                          onChange={(e) => setEditField({
                            ...editField, 
                            options: e.target.value.split('\n')
                          })}
                          onKeyDown={(e) => {
                            // Empêcher la soumission du formulaire lors de l'appui sur Entrée
                            if (e.key === 'Enter') {
                              e.stopPropagation();
                            }
                          }}
                          rows={5}
                        />
                        <p className="text-xs text-gray-500">
                          Appuyez sur Entrée pour créer une nouvelle option. {editField.options.length} option(s) ajoutée(s).
                        </p>
                      </div>
                    )}

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="edit-field-required"
                        checked={editField.is_required}
                        onChange={(e) => setEditField({...editField, is_required: e.target.checked})}
                        className="rounded"
                      />
                      <Label htmlFor="edit-field-required" className="cursor-pointer">
                        Champ obligatoire
                      </Label>
                    </div>

                    <div className="flex gap-2 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setIsEditFieldOpen(false);
                          setEditingField(null);
                        }}
                        className="flex-1"
                      >
                        Annuler
                      </Button>
                      <Button
                        onClick={handleUpdateField}
                        disabled={fieldLoading}
                        className="flex-1"
                      >
                        {fieldLoading ? 'Modification...' : 'Modifier'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {customFields.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>Aucun champ personnalisé pour le moment.</p>
                <p className="text-sm mt-2">Ajoutez des champs pour personnaliser vos fiches.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-sm text-gray-600 mb-4">
                  <p><strong>Note :</strong> Nom et Prénom sont des champs fixes.</p>
                </div>
                <div className="divide-y border rounded-lg">
                  {customFields.map((field) => (
                    <div key={field.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="flex items-center gap-2">
                          {field.field_type === 'text' && <Type className="h-4 w-4 text-gray-400" />}
                          {field.field_type === 'number' && <Hash className="h-4 w-4 text-gray-400" />}
                          {field.field_type === 'date' && <Calendar className="h-4 w-4 text-gray-400" />}
                          {field.field_type === 'select' && <List className="h-4 w-4 text-gray-400" />}
                          {field.field_type === 'checkbox' && <CheckSquare className="h-4 w-4 text-gray-400" />}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{field.name}</p>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <span className="capitalize">{field.field_type === 'select' ? 'Liste' : field.field_type}</span>
                            {field.is_required && (
                              <Badge variant="secondary" className="text-xs">Obligatoire</Badge>
                            )}
                            {!field.is_active && (
                              <Badge variant="outline" className="text-xs">Désactivé</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEditField(field)}
                          title="Modifier"
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteField(field.id)}
                          title="Supprimer"
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
              
              {/* Edit Field Dialog - Already present below */}
            </CardContent>
          </Card>

          {/* Note Statuses Management */}
          <Card className="mt-8 mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Tag className="h-5 w-5" />
                    Statuts de notes
                  </CardTitle>
                  <CardDescription>
                    Gérez les statuts pour organiser vos notes (Nouveau, En cours, Terminé, etc.)
                  </CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Label htmlFor="statuses-toggle" className="text-sm text-gray-600">
                    {areStatusesEnabled() ? 'Activé' : 'Désactivé'}
                  </Label>
                  <Switch
                    id="statuses-toggle"
                    checked={areStatusesEnabled()}
                    onCheckedChange={(checked) => updateSettings({ statuses_enabled: checked })}
                  />
                </div>
              </div>
            </CardHeader>
            {areStatusesEnabled() && (
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  {noteStatuses.length} statut{noteStatuses.length > 1 ? 's' : ''} configuré{noteStatuses.length > 1 ? 's' : ''}
                </p>
                <Dialog open={isAddStatusOpen} onOpenChange={setIsAddStatusOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Ajouter un statut
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Ajouter un nouveau statut</DialogTitle>
                      <DialogDescription>
                        Créez un statut personnalisé pour vos notes
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateStatus} className="space-y-4">
                      <div>
                        <Label htmlFor="status-name">Nom du statut *</Label>
                        <Input
                          id="status-name"
                          value={newStatus.name}
                          onChange={(e) => setNewStatus({...newStatus, name: e.target.value})}
                          placeholder="Ex: En attente"
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="status-color">Couleur *</Label>
                        <div className="flex gap-2 items-center">
                          <Input
                            id="status-color"
                            type="color"
                            value={newStatus.color}
                            onChange={(e) => setNewStatus({...newStatus, color: e.target.value})}
                            className="w-20 h-10"
                          />
                          <Input
                            type="text"
                            value={newStatus.color}
                            onChange={(e) => setNewStatus({...newStatus, color: e.target.value})}
                            placeholder="#3B82F6"
                            className="flex-1"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="status-order">Ordre d'affichage</Label>
                        <Input
                          id="status-order"
                          type="number"
                          value={newStatus.order}
                          onChange={(e) => setNewStatus({...newStatus, order: parseInt(e.target.value)})}
                          min="0"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Plus le nombre est petit, plus le statut apparaîtra en premier
                        </p>
                      </div>

                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="status-default"
                          checked={newStatus.is_default}
                          onChange={(e) => setNewStatus({...newStatus, is_default: e.target.checked})}
                          className="rounded"
                        />
                        <Label htmlFor="status-default" className="cursor-pointer">
                          Statut par défaut pour les nouvelles notes
                        </Label>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setIsAddStatusOpen(false)}
                          className="flex-1"
                        >
                          Annuler
                        </Button>
                        <Button type="submit" disabled={statusLoading} className="flex-1">
                          {statusLoading ? 'Création...' : 'Créer'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {noteStatuses.length > 0 && (
                <div className="space-y-2">
                  {noteStatuses.map((status) => (
                    <div
                      key={status.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: status.color }}
                        />
                        <div>
                          <span className="font-medium">{status.name}</span>
                          {status.is_default && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                              Par défaut
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditStatusClick(status)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteStatus(status.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Edit Status Dialog */}
              <Dialog open={isEditStatusOpen} onOpenChange={setIsEditStatusOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Modifier le statut</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleUpdateStatus} className="space-y-4">
                    <div>
                      <Label htmlFor="edit-status-name">Nom du statut *</Label>
                      <Input
                        id="edit-status-name"
                        value={editStatus.name}
                        onChange={(e) => setEditStatus({...editStatus, name: e.target.value})}
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="edit-status-color">Couleur *</Label>
                      <div className="flex gap-2 items-center">
                        <Input
                          id="edit-status-color"
                          type="color"
                          value={editStatus.color}
                          onChange={(e) => setEditStatus({...editStatus, color: e.target.value})}
                          className="w-20 h-10"
                        />
                        <Input
                          type="text"
                          value={editStatus.color}
                          onChange={(e) => setEditStatus({...editStatus, color: e.target.value})}
                          className="flex-1"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="edit-status-order">Ordre d'affichage</Label>
                      <Input
                        id="edit-status-order"
                        type="number"
                        value={editStatus.order}
                        onChange={(e) => setEditStatus({...editStatus, order: parseInt(e.target.value)})}
                        min="0"
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="edit-status-default"
                        checked={editStatus.is_default}
                        onChange={(e) => setEditStatus({...editStatus, is_default: e.target.checked})}
                        className="rounded"
                      />
                      <Label htmlFor="edit-status-default" className="cursor-pointer">
                        Statut par défaut
                      </Label>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsEditStatusOpen(false)}
                        className="flex-1"
                      >
                        Annuler
                      </Button>
                      <Button type="submit" disabled={statusLoading} className="flex-1">
                        {statusLoading ? 'Modification...' : 'Modifier'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardContent>
            )}
          </Card>

        {/* Additional Settings Cards */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>À propos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm text-gray-600">
              <p><strong>ConfiData Pro</strong> - Version 2.2</p>
              <p>Application de gestion professionnelle pour tous types de professionnels</p>
              <p>Données sécurisées et confidentielles</p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};


export default Reglages;
